// 로그인 필요 라우트 가드. 비로그인 시 로그인으로 보내고, 로그인 후 원래 자리 복귀.
import { useEffect, useState } from "react";
import { Navigate, Outlet, matchPath, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "context/AuthContext";
import { canReceiveQuoteAs, heldTypes } from "constants/permissions";
import { ROLE_LABELS } from "common/activeTypeGate";
import { openMemberType } from "util/openMemberType";
import EmptyView from "components/ui/EmptyView";
import { callGetLesson } from "api/MuyeonApi";

const RequireAuth = () => {
  const { isLoggedIn } = useAuth();
  const location = useLocation();

  if (isLoggedIn) return <Outlet />;
  const redirect = encodeURIComponent(location.pathname + location.search);
  return <Navigate to={`/login?redirect=${redirect}`} replace />;
};

/**
 * 레슨 제공자(강사·학원 원장) 전용 화면 가드 — meta.lessonProvider 라우트.
 *  2026-09-26: 무용수는 레슨 개설·시간관리·견적 응답 등 레슨 활동을 할 수 없다(강사 유형을 따야 함).
 *  활성유형 기준(Model B) — 강사를 보유했어도 무용수로 활동 중이면 전환을 먼저 권한다.
 *  화면은 LessonCreate 의 '권한 없음' 뷰와 같은 구성(EmptyView + btn-group)을 쓴다.
 */
export const RequireLessonProvider = () => {
  const { activeType } = useAuth();
  const location = useLocation();
  const allowed = canReceiveQuoteAs(activeType);
  // ★ 2026-10-29 학원 수업을 일반 유형에서 수정하려 하면 '강사로 전환'이 떠서 강사로 열렸다.
  //  어떤 유형으로 전환할지는 그 수업의 주인 유형으로 정한다 — 들어온 화면이 넘긴 값(state.providerRole),
  //  없으면 수정 화면(lessons/:id/edit)의 수업을 읽어 정한다.
  const editId = matchPath("/lessons/:id/edit", location.pathname)?.params?.id;
  const [lessonRole, setLessonRole] = useState(null);
  useEffect(() => {
    if (allowed || !editId || location.state?.providerRole) return undefined;
    let alive = true;
    callGetLesson(editId)
      .then((r) => { if (alive) setLessonRole(lessonProviderRole(r?.data ?? r, null)); })
      .catch(() => {});
    return () => { alive = false; };
  }, [allowed, editId, location.state?.providerRole]);
  if (allowed) return <Outlet />;
  return <LessonProviderSwitchView preferred={location.state?.providerRole || lessonRole} />;
};

/**
 * 레슨·수업을 관리할 유형 — 학원 이름으로 낸 수업(creatorType ACADEMY)이거나 내가 관리 학원이면 학원 원장,
 *  나머지는 강사. 주인 정보를 모르면 null(가드가 보유 유형 순서로 고른다).
 */
export const lessonProviderRole = (lesson, userId) => {
  if (!lesson) return null;
  if (lesson.creatorType === "ACADEMY") return "ACADEMY";
  if (userId && lesson.ownerAcademyId && Number(lesson.ownerAcademyId) === Number(userId)) return "ACADEMY";
  return lesson.creatorType === "TEACHER" ? "TEACHER" : null;
};

/**
 * '강사 유형으로 전환' 화면 — 레슨 제공자 화면 가드와, 강사 쪽 견적 채팅방(ChatRoom)이 함께 쓴다.
 *  알림(푸시·카카오 알림톡)으로 들어왔는데 일반 유형이면 여기서 전환하고 그 자리에서 이어 본다
 *  (setActiveType 후 부모가 다시 그려지며 원래 화면이 열린다).
 */
export const LessonProviderSwitchView = ({ title = "레슨은 강사·학원 원장 유형에서 운영할 수 있어요.", preferred = null }) => {
  const { user, activeType, setActiveType } = useAuth();
  const navigate = useNavigate();
  // 전환 가능한 제공자 유형(보유 중) — 수업 주인 유형(preferred)을 먼저, 없으면 강사 우선
  const order = preferred ? [preferred, "TEACHER", "ACADEMY"] : ["TEACHER", "ACADEMY"];
  const switchTo = order.find((t) => heldTypes(user).includes(t));
  return (
    <div id="sub_create" className="page">
      <div className="content-wrap">
        <EmptyView
          title={title}
          content={switchTo
            ? `${ROLE_LABELS[switchTo]} 유형으로 전환한 후 이용해 주세요.`
            : "레슨을 운영하려면 강사 유형을 추가하고 인증을 완료해 주세요."}
        />
        <div className="btn-group">
          <button type="button" className="btn-md outlined" onClick={() => navigate(-1)}>돌아가기</button>
          {switchTo ? (
            <button type="button" className="btn-md filled primary" onClick={() => setActiveType(switchTo)}>
              {ROLE_LABELS[switchTo]} 유형으로 전환
            </button>
          ) : (
            <button type="button" className="btn-md filled primary" onClick={() => openMemberType(navigate, activeType)}>회원유형 추가</button>
          )}
        </div>
      </div>
    </div>
  );
};

export default RequireAuth;
